// What the Review dialog shows for a Featured row with no agent's MLS ID paired to it (Unknown, Not Billable, or a
// Duplicate's extra row), or a Shared one, and what each choice does: assign the row's MLS ID to a billable lead owner,
// make its agent billable, or delete it from Featured Listings.
// Built from the agent MLS IDs (shared/agent-mls-ids); index.tsx beside it renders it and makes the write.
import { featuredRows } from "../featured-table";
import { AgentMlsIds } from "model/agent-mls-ids";

export type ReviewChoice = "assign" | "bill" | "delete";

export interface ReviewOption {
	choice: ReviewChoice;
	label: string;
	description: string;
}

// An agent in the Billable Lead Owner dropdown.
export interface LeadOwnerOption {
	agentId: number;
	name: string;
	// Why they can't take it, or null.
	disabledReason: string | null;
	// Shared: the agent already has the MLS ID on their profile. Assigning it to anyone else removes it from the holders.
	holder: boolean;
}

export interface ReviewPlan {
	clientBoardId: number;
	mlsId: string;
	state: "unknown" | "nonBillable" | "shared" | "duplicate";
	title: string;
	prompt: string;
	options: ReviewOption[];
	// Assign's dropdown: for a Shared row the holders and every billable agent with no MLS ID on the board, otherwise
	// every billable agent.
	leadOwners: LeadOwnerOption[];
	// Not Billable: the agent Make Billable is for.
	billAgent: { agentId: number; name: string } | null;
}

const first = (name: string) => name.split(" ")[0];
const listNames = (names: string[]) =>
	names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

const DELETE: ReviewOption = {
	choice: "delete",
	label: "Delete MLS ID",
	description: "Removes it from Featured Listings. Once it's removed, it can't be added back here.",
};

// Only an admin may make an agent billable; anyone else gets Delete alone on a Not Billable row.
export const reviewPlan = (agentMlsIds: AgentMlsIds, clientBoardId: number, admin = true): ReviewPlan | null => {
	const row = featuredRows(agentMlsIds).find((r) => r.clientBoardId === clientBoardId);
	if (!row || row.state === "aligned") {
		return null;
	}
	const board = agentMlsIds.boards.find((b) => b.boardId === row.boardId)?.name ?? "";
	const names = row.agentIds.map((id) => agentMlsIds.agents.get(id)?.name ?? "");
	const base = { clientBoardId, mlsId: row.mlsId, state: row.state };
	// Any billable agent can take an orphan: an agent may have more than one MLS ID on a board.
	const billableOwners = Array.from(agentMlsIds.agents.values())
		.filter((agent) => agent.billable)
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((agent) => ({ agentId: agent.id, name: agent.name, disabledReason: null, holder: false }));
	const assign: ReviewOption = { choice: "assign", label: "Assign to a Billable Lead Owner", description: "Adds it to the lead owner's profile, so it also powers their bio page and lead routing." };
	if (row.state === "shared") {
		// A billable agent with an MLS ID on the board, this one or another, is left out unless they hold this one.
		const onBoard = new Set(agentMlsIds.entries.filter((entry) => entry.boardId === row.boardId).map((entry) => entry.agentId));
		return {
			...base,
			title: "Assign MLS ID",
			prompt: `${row.mlsId} on ${board} is on ${listNames(names)}'s profiles. Assign it to one billable lead owner, or delete it.`,
			options: [
				{ choice: "assign", label: "Assign to a Billable Lead Owner", description: "It goes on the chosen lead owner's profile and is removed from the others." },
				DELETE,
			],
			leadOwners: [
				...row.agentIds.map((agentId) => ({
					agentId,
					name: agentMlsIds.agents.get(agentId)?.name ?? "",
					disabledReason: !agentMlsIds.agents.get(agentId)?.billable ? "not billable" : null,
					holder: true,
				})),
				...billableOwners.filter((owner) => !onBoard.has(owner.agentId)),
			],
			billAgent: null,
		};
	}
	if (row.state === "duplicate") {
		return {
			...base,
			title: "Assign MLS ID",
			prompt: `${row.mlsId} on ${board} is on Featured Listings more times than it's on ${listNames(names)}'s profiles. Delete this one, or assign it to a billable lead owner.`,
			options: [DELETE, assign],
			leadOwners: billableOwners,
			billAgent: null,
		};
	}
	// Not Billable, where every agent with the value isn't billable: make one billable. Where they're billable but keep
	// it off Featured Listings, it can only be deleted or assigned.
	const nonBillableAgent = row.state === "nonBillable" ? row.agentIds.find((id) => !agentMlsIds.agents.get(id)?.billable) : undefined;
	if (row.state === "nonBillable" && nonBillableAgent === undefined) {
		return {
			...base,
			title: "Assign MLS ID",
			prompt: `${row.mlsId} on ${board} is kept off Featured Listings on ${listNames(names)}'s profile. Delete it, or assign it to a billable lead owner.`,
			options: [DELETE, assign],
			leadOwners: billableOwners,
			billAgent: null,
		};
	}
	if (row.state === "nonBillable" && nonBillableAgent !== undefined) {
		const agentId = nonBillableAgent;
		const name = agentMlsIds.agents.get(agentId)?.name ?? "";
		return {
			...base,
			title: "Make Agent Billable",
			prompt: `${row.mlsId} on ${board} belongs to ${name}, but they aren't billable. Make them billable, or delete it.`,
			options: [
				...(admin ? [{
					choice: "bill" as const,
					label: `Make ${name} Billable`,
					description: `${first(name)} becomes a lead owner, and ${row.mlsId} stays assigned to them. Each billable lead owner increases your monthly bill.`,
				}] : []),
				DELETE,
			],
			leadOwners: [],
			billAgent: { agentId, name },
		};
	}
	// Unknown: any billable agent can take it.
	return {
		...base,
		title: "Assign MLS ID",
		prompt: `${row.mlsId} on ${board} isn't assigned to any agent. Delete it, or assign it to a billable lead owner.`,
		options: [assign, DELETE],
		leadOwners: billableOwners,
		billAgent: null,
	};
};

// The note under the choices. Assigning a Shared row says what the others lose; deleting it says they keep it.
export const reviewNote = (plan: ReviewPlan, choice: ReviewChoice, agentId: number | null): { text: string; severity: "warning" | "info" } | null => {
	if (plan.state !== "shared") {
		return null;
	}
	if (choice === "delete") {
		return { text: `${plan.mlsId} stays on each agent's profile for their bio page and lead routing.`, severity: "info" };
	}
	if (choice !== "assign" || agentId === null) {
		return null;
	}
	const others = plan.leadOwners.filter((o) => o.holder && o.agentId !== agentId).map((o) => o.name);
	return {
		text: `${plan.mlsId} will be removed from ${listNames(others)}'s profile, so its listings leave ${listNames(others.map((n) => `${first(n)}'s`))} bio page `
			+ `and its leads no longer route to ${others.map(first).join(" or ")}.`,
		severity: "warning",
	};
};

export const REVIEW_SAVE_LABEL: Record<ReviewChoice, string> = { assign: "Assign", bill: "Make Billable", delete: "Delete" };

// The toast after the write.
export const reviewDone = (plan: ReviewPlan, choice: ReviewChoice, agentName: string): string =>
	choice === "delete" ? `${plan.mlsId} deleted from Featured Listings.`
	: choice === "bill" ? `${agentName} is billable. ${plan.mlsId} is linked to them.`
	: `${plan.mlsId} is now assigned to ${agentName}.`;
